import { Typography } from "@material-ui/core"
import { HomePageCarStrip } from "api"
import { Desktop, Mobile } from "common/MediaQueries"
import { trackPlayWithTheColorClicks } from "common/analytics"
import slugify from "common/bit/utils/slugify"
import { ForwardedRef, forwardRef, useEffect, useRef, useState } from "react"
import ButtonBaseBlank from "reusable/ButtonBaseBlank"
import ColorCircle from "reusable/colors/ColorCircle"
import ColorCircleAnimation from "reusable/colors/ColorCircleAnimation"
import styled, { css, keyframes } from "styled-components"
import { Swiper as SwiperCore } from "swiper"
import { HEADER_HEIGHT_D, HEADER_HEIGHT_M } from "theme/shared"
import ColorImageSwiper from "./ColorImageSwiper"
import SwiperFooterText from "./SwiperFooterText"
import SwiperTopText from "./SwiperTopText"

const StyledWrap = styled.div`
  contain: paint;
  position: relative;
  z-index: 0;
  height: 660px;
  /* height: calc(100vh - ${HEADER_HEIGHT_M}px); */
  background-color: ${(p) => p.theme.colors.lightGrey};
  ${(p) => p.theme.breakpoints.up("md")} {
    min-height: calc(100vh - ${HEADER_HEIGHT_D}px);
    height: fit-content;
  }
  /* opacity: 0; */
`
export const StyledSwiperMain = styled.div`
  contain: paint;
  max-width: 100vw;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  ${(p) => p.theme.breakpoints.up("md")} {
    justify-content: center;
    min-height: calc(100vh - ${HEADER_HEIGHT_D}px);
    height: 100%;
  }
`
export const keyFrameCircleDesktop = keyframes`

  0% { clip-path: circle(
    25px at calc(100% - 100px)
      calc(100% + 100px)
  );
}

  80% { clip-path: circle(
    250% at calc(100% - 100px)
      calc(100% + 100px)
  );
}
  100% { clip-path: circle(
    110% at calc(100% - 100px)
      calc(120% + 100px)
  );
  }
`
export const keyFrameCircleMobile = keyframes`

  0% { clip-path: circle(
    50px at calc(100% - 30px) calc(100% - 110px)
  ); }

  80% { clip-path: circle(
    350%  at calc(100% - 30px) calc(100% - 110px)
  ); }
  100% { clip-path: circle(
    110%  at calc(100% - 30px) calc(100% - 110px)
  ); }
`
const StyledDuplicateSwiperMain = styled(StyledSwiperMain)<{ $start: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  clip-path: circle(50px at calc(100% - 30px) calc(100% - 110px));
  visibility: ${({ $start }) => ($start ? "visible" : "hidden")};
  animation: ${({ $start }) =>
    $start &&
    css`
      ${keyFrameCircleMobile} 1200ms  linear forwards
    `};
  ${(p) => p.theme.breakpoints.up("md")} {
    clip-path: circle(25px at calc(100% - 100px) calc(100% + 100px));
    animation: ${({ $start }) =>
      $start &&
      css`
        ${keyFrameCircleDesktop} 1200ms  linear forwards
      `};
  }
  background-color: ${(p) => p.theme.colors.lightGrey};
`

const StyledColorCircleAnimation = styled(ColorCircleAnimation)`
  top: 20px;
  right: 0px;
  left: auto;
  position: absolute;
`

const StyledAnimationWrapper = styled.div`
  position: absolute;
  bottom: 60px;
  right: -20px;
  ${(p) => p.theme.breakpoints.up("md")} {
    bottom: 40px;
    right: 50px;
  }
`

const shine = keyframes`
    0% {left: -200px}
    20% {left: 100%}
    100% {left: 100%}
`

const StyledColorCircle = styled(ColorCircle)`
  border: 4px solid #ffffff;
  /* cardShaddow */

  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  /* before */
  :before {
    /* content: "";
    position: absolute;
    width: 250px;
    height: 100%;
    background-image: linear-gradient(
      28deg,
      rgba(255, 255, 255, 0) 45%,
      rgba(255, 255, 255, 0.5),
      rgba(255, 255, 255, 0) 55%
    );
    top: 0;
    left: -200px;
    animation: ${shine} 4s infinite linear; /* Animation
    z-index: 1000;
    mix-blend-mode: soft-light;*/
  }
`
const StyledCircleText = styled(Typography)`
  text-align: center;

  ${(p) => p.theme.breakpoints.down("sm")} {
    color: ${(p) => p.theme.colors.white};
    inset: 0;
    inset-inline-start: 9px;
    padding: 6px;
    left: auto;
    position: absolute;
    text-shadow: 0px 4px 10px rgb(0 0 0 / 10%);
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    color: ${(p) => p.theme.colors.black};
    margin-top: 4px;
  }
`
type SwiperMainProps = {
  carsStrip: HomePageCarStrip[]
  resetSwiper: boolean
}

const SwiperMain = (
  { carsStrip, resetSwiper }: SwiperMainProps,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [colorIndex, setColorIndex] = useState(0)
  const [colorChange, setColorChange] = useState(false)
  const [firstSwiper, setFirstSwiper] = useState<SwiperCore | null>(null)
  const [secondSwiper, setSecondSwiper] = useState<SwiperCore | null>(null)
  const colorRef = useRef(null)
  const [carLink, setCarLink] = useState("")

  const nextColorIndex = carsStrip[0].modelColors[colorIndex + 1]
    ? colorIndex + 1
    : 0

  const handleColorChange = () => {
    setColorChange(true)
    colorRef.current = setTimeout(() => {
      setColorIndex(nextColorIndex)
      setColorChange(false)
    }, 960)
  }

  useEffect(() => {
    const card = carsStrip[activeIndex]
    const futureModel = card?.carState === "Future"
    const carLink = futureModel
      ? card.link
      : `/model/[modelId]/[modelSlug]`
          .replace("[modelId]", card.id.toString())
          .replace("[modelSlug]", slugify(card.name))

    setCarLink(carLink)
  }, [activeIndex])

  useEffect(() => {
    // Clear the interval when the component unmounts
    return () => {
      // clearTimeout(timerRef.current)
      clearTimeout(colorRef.current)
    }
  }, [])

  // set useEffect to set swipers refs
  useEffect(() => {
    return () => {
      setFirstSwiper(null)
      setSecondSwiper(null)
    }
  }, [])

  useEffect(() => {
    if (firstSwiper) {
      if (firstSwiper.realIndex !== 0) {
        firstSwiper.slideTo(1, 0)
        setActiveIndex(0)
      }
      setColorIndex(0)
    }
  }, [resetSwiper, firstSwiper])

  return (
    <StyledWrap id="colorSwiper" ref={ref}>
      <StyledSwiperMain>
        {carsStrip[activeIndex] && (
          <SwiperTopText carData={carsStrip[activeIndex]} carLink={carLink} />
        )}
        <ColorImageSwiper
          carsStrip={carsStrip}
          setActiveIndex={setActiveIndex}
          colorIndex={colorIndex}
          setControlledSwiper={setFirstSwiper}
          controlledSwiper={secondSwiper}
        />
        {carsStrip[activeIndex] && (
          <SwiperFooterText
            card={carsStrip[activeIndex]}
            activeIndex={colorIndex}
            link={carLink}
            colorIndex={colorIndex}
          />
        )}
      </StyledSwiperMain>

      <StyledDuplicateSwiperMain $start={colorChange}>
        {carsStrip[activeIndex] && (
          <SwiperTopText carData={carsStrip[activeIndex]} carLink={carLink} />
        )}
        <ColorImageSwiper
          carsStrip={carsStrip}
          setActiveIndex={setActiveIndex}
          colorIndex={nextColorIndex}
          setControlledSwiper={setSecondSwiper}
          controlledSwiper={firstSwiper}
        />
        {carsStrip[activeIndex] && (
          <SwiperFooterText
            card={carsStrip[activeIndex]}
            activeIndex={colorIndex}
            link={carLink}
            colorIndex={colorIndex}
          />
        )}
      </StyledDuplicateSwiperMain>

      <StyledAnimationWrapper onClick={() => trackPlayWithTheColorClicks()}>
        <ButtonBaseBlank onClick={handleColorChange}>
          <StyledColorCircle
            disableButton
            height={100}
            hex={carsStrip[0].modelColors[nextColorIndex].colorHex}
            imageUrl={
              carsStrip[0].modelColors[nextColorIndex].colorImage.imageUrl
            }
          />
          <Mobile>
            <StyledCircleText variant="body2">שחקו עם הצבע</StyledCircleText>
          </Mobile>
        </ButtonBaseBlank>
        <Desktop>
          <StyledCircleText variant="body2">שחקו עם הצבע</StyledCircleText>
        </Desktop>
        <StyledColorCircleAnimation
          animationFlag={colorChange}
          hex={
            carsStrip[0].modelColors[colorIndex + 1]
              ? carsStrip[0].modelColors[colorIndex + 1].colorHex
              : carsStrip[0].modelColors[0].colorHex
          }
        />
      </StyledAnimationWrapper>
    </StyledWrap>
  )
}

export default forwardRef(SwiperMain)
